import React, { useEffect, useRef } from 'react';
import './StaticBackground.css';

// Your static generator class
class OrangeStaticGenerator {
  constructor(canvas) {
    this.canvas = canvas;
    if (!this.canvas) return;
    
    this.ctx = this.canvas.getContext('2d');
    this.animationId = null;
    this.isRunning = true;
    
    // Performance settings
    this.intensity = 0.75;
    this.speed = 100;
    this.grainSize = 3;
    this.blackLevel = 30;
    this.bandSpeed = 20;
    this.lastTime = 0;
    
    // Scrolling bands
    this.bands = [];
    this.initBands();
    
    // Noise areas for non-uniform static
    this.noiseAreas = [];
    this.initNoiseAreas();
    
    this.setupCanvas();
    this.animate();
  }
  
  setupCanvas() {
    const updateCanvasSize = () => {
      this.canvas.width = window.innerWidth;
      this.canvas.height = window.innerHeight;
      this.canvas.style.width = window.innerWidth + 'px';
      this.canvas.style.height = window.innerHeight + 'px';
    };
    
    updateCanvasSize();
    window.addEventListener('resize', () => {
      updateCanvasSize();
      this.initBands();
      this.initNoiseAreas();
    });
  }
  
  initBands() {
    this.bands = [];
    const numBands = Math.floor(Math.random() * 3) + 3;
    
    for (let i = 0; i < numBands; i++) {
      this.bands.push({
        y: Math.random() * (window.innerHeight + 200) - 100,
        height: Math.random() * 80 + 20,
        speed: (Math.random() * 2 + 0.5) * this.bandSpeed,
        opacity: Math.random() * 0.3 + 0.1,
        distortion: Math.random() * 0.5 + 0.5
      });
    }
  }
  
  initNoiseAreas() {
    this.noiseAreas = [];
    const numAreas = Math.floor(Math.random() * 4) + 2;
    
    for (let i = 0; i < numAreas; i++) {
      this.noiseAreas.push({
        x: Math.random() * window.innerWidth,
        y: Math.random() * window.innerHeight,
        radius: Math.random() * 200 + 100,
        intensity: Math.random() * 0.5 + 0.2,
        drift: {
          x: (Math.random() - 0.5) * 3,
          y: (Math.random() - 0.5) * 3
        },
        pulseSpeed: Math.random() * 0.005 + 0.002,
        baseIntensity: Math.random() * 0.5 + 0.2
      });
    }
  }
  
  updateBands() {
    for (let i = this.bands.length - 1; i >= 0; i--) {
      this.bands[i].y -= this.bands[i].speed;
      
      if (this.bands[i].y + this.bands[i].height < -50) {
        this.bands.splice(i, 1);
      }
    }
    
    while (this.bands.length < 5 && Math.random() < 0.02) {
      this.bands.push({
        y: window.innerHeight + Math.random() * 100,
        height: Math.random() * 80 + 20,
        speed: (Math.random() * 2 + 0.5) * this.bandSpeed,
        opacity: Math.random() * 0.3 + 0.1,
        distortion: Math.random() * 0.5 + 0.5
      });
    }
  }
  
  updateNoiseAreas() {
    this.noiseAreas.forEach(area => {
      area.x += area.drift.x;
      area.y += area.drift.y;
      area.intensity = area.baseIntensity + Math.sin(Date.now() * area.pulseSpeed) * 0.3;
      
      if (area.x < -area.radius) area.x = window.innerWidth + area.radius;
      if (area.x > window.innerWidth + area.radius) area.x = -area.radius;
      if (area.y < -area.radius) area.y = window.innerHeight + area.radius;
      if (area.y > window.innerHeight + area.radius) area.y = -area.radius;
    });
    
    if (Math.random() < 0.005) {
      if (this.noiseAreas.length < 6 && Math.random() < 0.7) {
        this.noiseAreas.push({
          x: Math.random() * window.innerWidth,
          y: Math.random() * window.innerHeight,
          radius: Math.random() * 200 + 100,
          intensity: Math.random() * 0.5 + 0.2,
          drift: {
            x: (Math.random() - 0.5) * 3,
            y: (Math.random() - 0.5) * 3
          },
          pulseSpeed: Math.random() * 0.005 + 0.002,
          baseIntensity: Math.random() * 0.5 + 0.2
        });
      } else if (this.noiseAreas.length > 2) {
        this.noiseAreas.splice(Math.floor(Math.random() * this.noiseAreas.length), 1);
      }
    }
  }
  
  getLocalIntensity(x, y) {
    let totalIntensity = this.intensity;
    
    this.noiseAreas.forEach(area => {
      const distance = Math.sqrt((x - area.x) ** 2 + (y - area.y) ** 2);
      if (distance < area.radius) {
        const influence = 1 - (distance / area.radius);
        const smoothInfluence = influence * influence * (3 - 2 * influence);
        totalIntensity += area.intensity * smoothInfluence;
      }
    });
    
    return Math.min(1, totalIntensity);
  }
  
  drawBands() {
    this.bands.forEach(band => {
      this.ctx.save();
      this.ctx.globalAlpha = band.opacity;
      this.ctx.fillStyle = '#000000';
      
      const segments = 10;
      const segmentHeight = band.height / segments;
      
      for (let i = 0; i < segments; i++) {
        const segmentY = band.y + (i * segmentHeight);
        const offset = Math.sin(segmentY * 0.1 + Date.now() * 0.001) * band.distortion * 5;
        
        this.ctx.fillRect(
          offset, 
          segmentY, 
          window.innerWidth - Math.abs(offset), 
          segmentHeight + 1
        );
      }
      
      this.ctx.restore();
    });
  }
  
  generateStatic() {
    this.ctx.fillStyle = '#ff5400';
    this.ctx.fillRect(0, 0, window.innerWidth, window.innerHeight);
    
    this.ctx.fillStyle = '#000000';
    
    const pixelSize = this.grainSize;
    const cols = Math.floor(window.innerWidth / pixelSize);
    const rows = Math.floor(window.innerHeight / pixelSize);
    
    for (let x = 0; x < cols; x++) {
      for (let y = 0; y < rows; y++) {
        const pixelX = x * pixelSize + pixelSize / 2;
        const pixelY = y * pixelSize + pixelSize / 2;
        const localIntensity = this.getLocalIntensity(pixelX, pixelY);
        
        if (Math.random() < localIntensity) {
          const opacity = Math.random() * (this.blackLevel / 100);
          this.ctx.globalAlpha = opacity;
          this.ctx.fillRect(x * pixelSize, y * pixelSize, pixelSize, pixelSize);
        }
      }
    }
    
    this.ctx.globalAlpha = 1.0;
    this.drawBands();
  }
  
  animate(currentTime = 0) {
    if (!this.isRunning) return;
    
    if (currentTime - this.lastTime >= this.speed) {
      this.updateBands();
      this.updateNoiseAreas();
      this.generateStatic();
      this.lastTime = currentTime;
    }
    
    this.animationId = requestAnimationFrame((time) => this.animate(time));
  }
  
  pause() {
    this.isRunning = false;
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
  }
  
  resume() {
    this.isRunning = true;
    this.animate();
  }
}

const StaticBackground = () => {
  const canvasRef = useRef(null);
  const generatorRef = useRef(null);

  useEffect(() => {
    // Wait for next tick to ensure canvas is in DOM
    const timer = setTimeout(() => {
      if (canvasRef.current && !generatorRef.current) {
        console.log('Initializing static generator...');
        const generator = new OrangeStaticGenerator(canvasRef.current);
        generatorRef.current = generator;
        window.staticGenerator = generator;
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      if (generatorRef.current) {
        generatorRef.current.pause();
        generatorRef.current = null;
      }
      window.staticGenerator = null;
    };
  }, []);

  return (
    <canvas 
      ref={canvasRef}
      id="staticCanvas" 
      className="static-canvas"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: -1,
        opacity: 1
      }}
    />
  );
};

export default StaticBackground;